import http from '../ApiHttp';
import { Datalayer, DatalayerEventLabel } from '../models/Datalayer';
import { Cached } from '../utilities/Cached';

const ROUTE = process.env.REACT_APP_URL_API_DATALAYER || '';

declare global {
  interface Window {
    dataLayer?: Datalayer[];
  }
}

export class DatalayerService {
  private static _instance: DatalayerService;
  private route;
  private dataLayer: Datalayer[];
  private lastPageViewUrl: string | null;
  private customizationData: Cached<string | null, Promise<Datalayer>> | null = null;
  private _resolveCustomizationDataFilling?: () => void;
  readonly customizationDataFilling: Promise<void>;

  private constructor() {
    this.route = ROUTE;
    window.dataLayer = window.dataLayer || [];
    this.dataLayer = window.dataLayer;
    this.lastPageViewUrl = null;
    this.customizationDataFilling = new Promise((resolve) => {
      this._resolveCustomizationDataFilling = resolve;
    });
  }

  public static get instance(): DatalayerService {
    if (!DatalayerService._instance) {
      DatalayerService._instance = new DatalayerService();
    }

    return this._instance;
  }

  public loadCustomizationData(organizationId: string | null): Promise<Datalayer> {
    this.customizationData ??= new Cached((organizationId) =>
      http.get(this.route || '', { params: { organizationId } }).then((res) => res.data)
    );

    const result = this.customizationData.get(organizationId, [organizationId ?? '']);
    this._resolveCustomizationDataFilling?.();
    return result;
  }

  public push(data: Datalayer) {
    if (data.event === 'pageview') {
      return this.pushPageView(data);
    }

    return this.dataLayer.push(data);
  }

  public async pushPageView(
    data: Datalayer | Record<string, never> = {},
    options?: { waitForCustomizationData?: boolean }
  ): Promise<number> {
    const waitForCustomizationData = options?.waitForCustomizationData ?? false;
    if (waitForCustomizationData) {
      await this.customizationDataFilling;
    }
    const pageUrl = window.location.href;
    if (pageUrl === this.lastPageViewUrl) {
      return this.dataLayer.length;
    }

    this.lastPageViewUrl = pageUrl;
    const pageTitle = document.title;
    const previousUrl = document.referrer ?? null;

    return this.dataLayer.push({
      ...((await this.customizationData?.cache) ?? {}),
      eventLabel: data.eventLabel ?? DatalayerEventLabel.DEFAULT,
      eventAction: data.eventAction ?? 'MAT',
      ...(data as Partial<Datalayer>),
      event: 'pageview',
      pageUrl,
      pageTitle,
      previousUrl,
    });
  }
}
